import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { exportCSV, csvDateStamp } from '../../utils/exportCSV';

// ─── Types ────────────────────────────────────────────────────────────────────

type StdRole = 'admin' | 'supervisor' | 'operator' | 'viewer' | 'readonly';

interface Permission {
  id: string;
  label: string;
  category: string;
  description: string;
}

interface CustomRole {
  id: string;
  name: string;
  color: string;
  permissions: string[];   // list of perm IDs that are granted
  created_at?: string;
  created_by?: string;
}

interface OrgUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  role: StdRole;
  status: 'active' | 'suspended' | 'pending';
  last_login: string;
  mfa_enabled: boolean;
  created_at: string;
  organisation_id: number;
  org_name?: string;
  // custom_permissions: flat map perm_id -> true (grant) | false (revoke) | absent (inherit)
  custom_permissions: Record<string, boolean>;
}

// ─── Expanded permissions catalogue (39 permissions, 10 categories) ──────────

const ALL_PERMISSIONS: Permission[] = [
  // Alarms
  { id:'alarm.view',       category:'Alarms',          label:'View Alarm Feed',          description:'See live and historical alarm feed' },
  { id:'alarm.ack',        category:'Alarms',          label:'Acknowledge Alarms',        description:'Mark alarms as acknowledged' },
  { id:'alarm.close',      category:'Alarms',          label:'Close / Resolve Alarms',    description:'Close and resolve active alarms' },
  { id:'alarm.suppress',   category:'Alarms',          label:'Create Suppressions',       description:'Schedule alarm suppression windows' },
  { id:'alarm.escalate',   category:'Alarms',          label:'Escalate Alarms',           description:'Manually escalate alarms up the chain' },
  // Access Control
  { id:'access.view',      category:'Access Control',  label:'View Access Events',        description:'See door events and access log' },
  { id:'access.command',   category:'Access Control',  label:'Remote Door Commands',      description:'Lock, unlock, and open doors remotely' },
  { id:'access.credentials', category:'Access Control', label:'Manage Credentials',       description:'Add/modify/remove access credentials and cards' },
  { id:'access.export',    category:'Access Control',  label:'Export Access Logs',        description:'Download access event logs (CSV)' },
  // Video
  { id:'video.view',       category:'Video',           label:'View Live Video',           description:'Access live camera feeds' },
  { id:'video.playback',   category:'Video',           label:'Video Playback',            description:'Review recorded footage' },
  { id:'video.export',     category:'Video',           label:'Export Footage',            description:'Download and export video clips' },
  { id:'video.ptz',        category:'Video',           label:'PTZ Control',               description:'Control pan-tilt-zoom cameras' },
  // Jobs & Incidents
  { id:'job.view',         category:'Jobs',            label:'View Jobs',                 description:'See job list and full details' },
  { id:'job.create',       category:'Jobs',            label:'Create Jobs',               description:'Create new jobs and work orders' },
  { id:'job.edit',         category:'Jobs',            label:'Edit Jobs',                 description:'Update job status, notes, and attachments' },
  { id:'job.close',        category:'Jobs',            label:'Close Jobs',                description:'Close and resolve jobs' },
  { id:'job.approve',      category:'Jobs',            label:'Approve Jobs',              description:'Supervisor sign-off on completed jobs' },
  { id:'job.assign',       category:'Jobs',            label:'Assign Jobs',               description:'Assign jobs to specific staff members' },
  // Guard Tours
  { id:'tours.view',       category:'Guard Tours',     label:'View Tours',                description:'View patrol routes and completion history' },
  { id:'tours.create',     category:'Guard Tours',     label:'Create / Edit Routes',      description:'Design and modify patrol routes and checkpoints' },
  { id:'tours.execute',    category:'Guard Tours',     label:'Execute Patrols',           description:'Start and complete patrol tours in-app' },
  // Lone Worker
  { id:'lw.view',          category:'Lone Worker',     label:'View Sessions',             description:'See all active lone worker sessions' },
  { id:'lw.escalate',      category:'Lone Worker',     label:'Escalate Overdue Workers',  description:'Trigger emergency escalation for overdue check-ins' },
  { id:'lw.manage',        category:'Lone Worker',     label:'Manage Settings',           description:'Configure check-in intervals and escalation rules' },
  // Shift Handover
  { id:'handover.view',    category:'Shift Handover',  label:'View Handovers',            description:'Read shift handover reports' },
  { id:'handover.create',  category:'Shift Handover',  label:'Create Handovers',          description:'Write and submit shift handover notes' },
  { id:'handover.approve', category:'Shift Handover',  label:'Approve Handovers',         description:'Formally sign off on shift handovers' },
  // Reports
  { id:'report.view',      category:'Reports',         label:'View Report Library',       description:'Access the full report library' },
  { id:'report.run',       category:'Reports',         label:'Run Reports',               description:'Generate on-demand reports' },
  { id:'report.export',    category:'Reports',         label:'Export Reports',            description:'Download report data (PDF/CSV)' },
  { id:'report.schedule',  category:'Reports',         label:'Schedule Reports',          description:'Set up automated scheduled report delivery' },
  // Assets
  { id:'asset.view',       category:'Assets',          label:'View Asset Registry',       description:'Browse the asset inventory' },
  { id:'asset.edit',       category:'Assets',          label:'Edit Assets',               description:'Add, modify, and update asset records' },
  { id:'asset.decommission', category:'Assets',        label:'Decommission Assets',       description:'Mark assets as decommissioned or disposed' },
  // Admin
  { id:'admin.users',      category:'Admin',           label:'User Management',           description:'Create, edit, suspend users in this organisation' },
  { id:'admin.roles',      category:'Admin',           label:'Role Templates',            description:'Create and edit custom role permission templates' },
  { id:'admin.settings',   category:'Admin',           label:'System Settings',           description:'Configure integrations, SMTP, and platform settings' },
  { id:'admin.audit',      category:'Admin',           label:'View Audit Trail',          description:'Access the full audit log of all user actions' },
];

const CATEGORIES = Array.from(new Set(ALL_PERMISSIONS.map(p => p.category)));

// ─── Default permission sets per standard role ────────────────────────────────

const STD_ROLE_PERMS: Record<StdRole, string[]> = {
  admin:      ALL_PERMISSIONS.map(p => p.id),
  supervisor: ['alarm.view','alarm.ack','alarm.close','alarm.suppress','alarm.escalate','access.view','access.command','access.export','video.view','video.playback','video.export','video.ptz','job.view','job.create','job.edit','job.close','job.approve','job.assign','tours.view','tours.create','tours.execute','lw.view','lw.escalate','lw.manage','handover.view','handover.create','handover.approve','report.view','report.run','report.export','report.schedule','asset.view','asset.edit','admin.audit'],
  operator:   ['alarm.view','alarm.ack','alarm.close','access.view','access.command','video.view','video.playback','video.ptz','job.view','job.create','job.edit','job.close','tours.view','tours.execute','lw.view','lw.escalate','handover.view','handover.create','report.view','asset.view'],
  viewer:     ['alarm.view','access.view','video.view','job.view','tours.view','lw.view','handover.view','report.view','asset.view'],
  readonly:   ['alarm.view','access.view','job.view'],
};

const ROLE_META: Record<StdRole, { label: string; color: string }> = {
  admin:      { label:'Admin',      color:'#ef4444' },
  supervisor: { label:'Supervisor', color:'#f59e0b' },
  operator:   { label:'Operator',   color:'#3b82f6' },
  viewer:     { label:'Viewer',     color:'#22c55e' },
  readonly:   { label:'Read Only',  color:'#555555' },
};

const STATUS_COLOR: Record<string, string> = { active:'#22c55e', suspended:'#ef4444', pending:'#f59e0b' };

const ROLE_ORDER: StdRole[] = ['admin','supervisor','operator','viewer','readonly'];
const ROLE_COLORS = ['#ef4444','#f59e0b','#3b82f6','#a855f7','#06b6d4','#10b981','#ec4899','#f97316','#6366f1','#22c55e'];

const tok = () => localStorage.getItem('lemai_token') || '';
const authH = () => ({ 'Content-Type':'application/json', Authorization:`Bearer ${tok()}` });

// ─── Inline Permission Matrix (reusable) ──────────────────────────────────────
// mode='role'   — full checkboxes, click to toggle, all-in or all-out per permission
// mode='user'   — three-state: null=inherit, true=grant, false=revoke

interface PermMatrixProps {
  grants: Set<string>;                 // currently granted permission IDs
  onChange?: (id: string) => void;
  locked?: boolean;
  compact?: boolean;
}

function PermMatrix({ grants, onChange, locked, compact }: PermMatrixProps) {
  return (
    <div>
      {CATEGORIES.map(cat => (
        <div key={cat} style={{ marginBottom: compact ? '8px' : '12px' }}>
          <div style={{ fontSize:'10px', fontWeight:700, color:'#555555', textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:'5px', paddingBottom:'3px', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>{cat}</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:'5px' }}>
            {ALL_PERMISSIONS.filter(p => p.category === cat).map(perm => {
              const on = grants.has(perm.id);
              return (
                <button key={perm.id}
                  title={perm.description}
                  onClick={() => !locked && onChange?.(perm.id)}
                  style={{
                    padding:'4px 10px', fontSize:'11px', fontWeight:600, borderRadius:'6px', cursor: locked ? 'default' : 'pointer',
                    background: on ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${on ? 'rgba(34,197,94,0.35)' : 'rgba(255,255,255,0.07)'}`,
                    color: on ? '#22c55e' : '#555555',
                    transition:'all 0.12s',
                  }}>
                  {on ? '✓ ' : ''}{perm.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── User Override Matrix ─────────────────────────────────────────────────────
// Shows inherited permissions from role + allows per-user overrides
// Override state: undefined=inherit, true=grant, false=revoke

interface UserPermMatrixProps {
  baseRole: StdRole;
  overrides: Record<string, boolean>;   // perm_id -> true/false
  onChange: (id: string) => void;
}

function UserPermMatrix({ baseRole, overrides, onChange }: UserPermMatrixProps) {
  const rolePerms = new Set(STD_ROLE_PERMS[baseRole] || []);
  return (
    <div>
      <div style={{ fontSize:'11px', color:'#555555', marginBottom:'10px', lineHeight:'1.5' }}>
        <span style={{ display:'inline-block', width:'10px', height:'10px', borderRadius:'3px', background:'rgba(34,197,94,0.15)', border:'1px solid rgba(34,197,94,0.35)', marginRight:'4px', verticalAlign:'middle' }} />inherited from role &nbsp;
        <span style={{ display:'inline-block', width:'10px', height:'10px', borderRadius:'3px', background:'rgba(59,130,246,0.2)', border:'1px solid rgba(59,130,246,0.5)', marginRight:'4px', verticalAlign:'middle' }} />granted (override) &nbsp;
        <span style={{ display:'inline-block', width:'10px', height:'10px', borderRadius:'3px', background:'rgba(239,68,68,0.15)', border:'1px solid rgba(239,68,68,0.35)', marginRight:'4px', verticalAlign:'middle' }} />revoked (override)
      </div>
      {CATEGORIES.map(cat => (
        <div key={cat} style={{ marginBottom:'10px' }}>
          <div style={{ fontSize:'10px', fontWeight:700, color:'#555555', textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:'5px', paddingBottom:'3px', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>{cat}</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:'4px' }}>
            {ALL_PERMISSIONS.filter(p => p.category === cat).map(perm => {
              const fromRole = rolePerms.has(perm.id);
              const ov = overrides[perm.id];
              const isGranted = ov === true;
              const isRevoked = ov === false;
              const inherited = ov === undefined;
              let bg = 'rgba(255,255,255,0.04)';
              let border = 'rgba(255,255,255,0.07)';
              let color = '#555555';
              let prefix = '';
              if (isGranted)       { bg='rgba(59,130,246,0.15)'; border='rgba(59,130,246,0.45)'; color='#60a5fa'; prefix='+ '; }
              else if (isRevoked)  { bg='rgba(239,68,68,0.12)'; border='rgba(239,68,68,0.35)'; color='#f87171'; prefix='✕ '; }
              else if (fromRole)   { bg='rgba(34,197,94,0.08)'; border='rgba(34,197,94,0.25)'; color='#4ade80'; }
              return (
                <button key={perm.id}
                  title={`${perm.description}\nClick to cycle: inherit → grant → revoke`}
                  onClick={() => onChange(perm.id)}
                  style={{ padding:'4px 10px', fontSize:'11px', fontWeight:600, borderRadius:'6px', cursor:'pointer', background:bg, border:`1px solid ${border}`, color, transition:'all 0.12s' }}>
                  {prefix}{perm.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Add User Modal ───────────────────────────────────────────────────────────

interface AddUserModalProps {
  isSuperAdmin: boolean;
  callerOrgId: number | null;
  customRoles: CustomRole[];
  onAdd: (u: OrgUser) => void;
  onClose: () => void;
}

function AddUserModal({ isSuperAdmin, callerOrgId, customRoles, onAdd, onClose }: AddUserModalProps) {
  const { toast } = useToast();
  const [orgs, setOrgs] = useState<{id:number;name:string}[]>([]);
  const [form, setForm] = useState({
    first_name:'', last_name:'', email:'', password:'', confirm:'',
    role: 'operator' as StdRole,
    organisation_id: callerOrgId ?? 0,
    customRoleId: '',   // if set, apply that template's permissions
  });
  const [errors, setErrors] = useState<Record<string,string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin) return;
    fetch('/api/admin/orgs', { headers: authH() })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.orgs) setOrgs(d.orgs); })
      .catch(() => {});
  }, [isSuperAdmin]);

  function set(k: string, v: string) { setForm(p => ({...p,[k]:v})); setErrors(p => ({...p,[k]:''})); }

  function validate() {
    const e: Record<string,string> = {};
    if (!form.first_name.trim()) e.first_name = 'Required';
    if (!form.last_name.trim())  e.last_name  = 'Required';
    if (!form.email.trim() || !form.email.includes('@')) e.email = 'Valid email required';
    if (!form.password || form.password.length < 8) e.password = 'Min 8 characters';
    if (form.password !== form.confirm) e.confirm = 'Passwords do not match';
    if (isSuperAdmin && !form.organisation_id) e.organisation_id = 'Select an organisation';
    return e;
  }

  async function handleSave() {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      // If a custom role template is selected, copy its permissions as overrides
      let custom_permissions: Record<string,boolean> = {};
      if (form.customRoleId) {
        const tmpl = customRoles.find(r => r.id === form.customRoleId);
        if (tmpl) {
          // Grant all permissions in the template
          tmpl.permissions.forEach(pid => { custom_permissions[pid] = true; });
        }
      }
      const res = await fetch('/api/admin/org-users', {
        method:'POST',
        headers: authH(),
        body: JSON.stringify({
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email.trim().toLowerCase(),
          password: form.password,
          role: form.role,
          organisation_id: form.organisation_id,
          custom_permissions,
        }),
      });
      const d = await res.json();
      if (!res.ok) { setErrors({ email: d.error || 'Failed to create user' }); return; }
      toast(`User ${d.first_name} ${d.last_name} created`, 'success');
      onAdd(d);
      onClose();
    } catch {
      setErrors({ email: 'Network error — try again' });
    } finally {
      setSaving(false);
    }
  }

  const inp = (label: string, key: string, type = 'text', ph = '') => (
    <div>
      <label style={{ fontSize:'12px', fontWeight:600, color:'#a0a0a0', display:'block', marginBottom:'5px' }}>{label}</label>
      <input type={type} value={(form as any)[key]} onChange={e => set(key, e.target.value)} placeholder={ph}
        style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:`1px solid ${errors[key]?'#ef4444':'rgba(255,255,255,0.1)'}`, borderRadius:'4px', padding:'9px 12px', color:'#e5e5e5', fontSize:'13px', outline:'none', boxSizing:'border-box' as const }} />
      {errors[key] && <div style={{ fontSize:'11px', color:'#ef4444', marginTop:'3px' }}>{errors[key]}</div>}
    </div>
  );

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', zIndex:500, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}>
      <div style={{ background:'#0a0a0a', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'5px', width:'100%', maxWidth:'480px', maxHeight:'90vh', overflowY:'auto' }}>
        <div style={{ padding:'16px 20px', borderBottom:'1px solid rgba(255,255,255,0.07)', display:'flex', justifyContent:'space-between', alignItems:'center', position:'sticky', top:0, background:'#0a0a0a', zIndex:1 }}>
          <div style={{ fontWeight:700, fontSize:'15px', color:'white' }}>New User</div>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,0.06)', border:'none', borderRadius:'6px', color:'#555555', width:'28px', height:'28px', fontSize:'14px', cursor:'pointer' }}>✕</button>
        </div>
        <div style={{ padding:'20px', display:'grid', gap:'14px' }}>
          {isSuperAdmin && (
            <div>
              <label style={{ fontSize:'12px', fontWeight:600, color:'#a0a0a0', display:'block', marginBottom:'5px' }}>Organisation</label>
              <select value={form.organisation_id} onChange={e => set('organisation_id', e.target.value)}
                style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:`1px solid ${errors.organisation_id?'#ef4444':'rgba(255,255,255,0.1)'}`, borderRadius:'4px', padding:'9px 12px', color:'#e5e5e5', fontSize:'13px', cursor:'pointer' }}>
                <option value="">— Select org —</option>
                {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
              {errors.organisation_id && <div style={{ fontSize:'11px', color:'#ef4444', marginTop:'3px' }}>{errors.organisation_id}</div>}
            </div>
          )}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
            {inp('First Name', 'first_name')}
            {inp('Last Name', 'last_name')}
          </div>
          {inp('Email Address', 'email', 'email', 'user@council.nsw.gov.au')}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
            {inp('Temporary Password', 'password', 'password', 'Min 8 characters')}
            {inp('Confirm Password', 'confirm', 'password', 'Re-enter password')}
          </div>
          <div>
            <label style={{ fontSize:'12px', fontWeight:600, color:'#a0a0a0', display:'block', marginBottom:'5px' }}>Base Role</label>
            <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
              {ROLE_ORDER.map(r => (
                <button key={r} onClick={() => set('role', r)}
                  style={{ padding:'6px 14px', borderRadius:'4px', border:`1px solid ${form.role===r?ROLE_META[r].color:'rgba(255,255,255,0.08)'}`, background:form.role===r?`${ROLE_META[r].color}18`:'transparent', color:form.role===r?ROLE_META[r].color:'#555555', fontSize:'12px', fontWeight:600, cursor:'pointer' }}>
                  {ROLE_META[r].label}
                </button>
              ))}
            </div>
          </div>
          {customRoles.length > 0 && (
            <div>
              <label style={{ fontSize:'12px', fontWeight:600, color:'#a0a0a0', display:'block', marginBottom:'5px' }}>Apply Permission Template <span style={{ fontWeight:400, color:'#555555' }}>(optional)</span></label>
              <select value={form.customRoleId} onChange={e => set('customRoleId', e.target.value)}
                style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'4px', padding:'9px 12px', color:'#e5e5e5', fontSize:'13px', cursor:'pointer' }}>
                <option value="">— None (use role defaults) —</option>
                {customRoles.map(r => <option key={r.id} value={r.id}>{r.name} ({r.permissions.length} perms)</option>)}
              </select>
              <div style={{ fontSize:'10px', color:'#555555', marginTop:'4px' }}>Copies the template's permission grants as individual overrides on this user.</div>
            </div>
          )}
          <div style={{ display:'flex', gap:'8px', paddingTop:'4px' }}>
            <button onClick={onClose} style={{ flex:1, padding:'10px', background:'#111111', border:'1px solid #1a1a1a', borderRadius:'4px', color:'#555555', fontSize:'13px', cursor:'pointer' }}>Cancel</button>
            <button onClick={handleSave} disabled={saving}
              style={{ flex:2, padding:'10px', background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', border:'none', borderRadius:'4px', color:'white', fontSize:'13px', fontWeight:700, cursor:'pointer', opacity:saving?0.6:1 }}>
              {saving ? 'Creating…' : 'Create User'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── User Drawer ──────────────────────────────────────────────────────────────

interface UserDrawerProps {
  user: OrgUser;
  customRoles: CustomRole[];
  isSuperAdmin: boolean;
  isOrgAdmin: boolean;
  onClose: () => void;
  onSaved: (updated: OrgUser) => void;
  onDeleted: (id: number) => void;
}

function UserDrawer({ user: init, customRoles, isSuperAdmin, isOrgAdmin, onClose, onSaved, onDeleted }: UserDrawerProps) {
  const { toast } = useToast();
  const [user, setUser] = useState<OrgUser>({ ...init, custom_permissions: { ...(init.custom_permissions || {}) } });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  function cyclePermOverride(permId: string) {
    setUser(prev => {
      const ov = { ...(prev.custom_permissions || {}) };
      const current = ov[permId];
      if (current === undefined) {
        ov[permId] = true;   // inherit → grant
      } else if (current === true) {
        ov[permId] = false;  // grant → revoke
      } else {
        delete ov[permId];   // revoke → inherit
      }
      return { ...prev, custom_permissions: ov };
    });
  }

  function applyTemplate(tmplId: string) {
    const tmpl = customRoles.find(r => r.id === tmplId);
    if (!tmpl) return;
    const ov: Record<string,boolean> = {};
    tmpl.permissions.forEach(pid => { ov[pid] = true; });
    setUser(prev => ({ ...prev, custom_permissions: ov }));
    toast(`Template "${tmpl.name}" applied — save to confirm`, 'info');
  }

  function resetOverrides() {
    setUser(prev => ({ ...prev, custom_permissions: {} }));
  }

  async function handleSave() {
    setSaving(true);
    try {
      const res = await fetch(`/api/admin/org-users/${user.id}`, {
        method:'PUT', headers: authH(),
        body: JSON.stringify({ role: user.role, is_active: user.status==='active', custom_permissions: user.custom_permissions }),
      });
      const d = await res.json();
      if (!res.ok) { toast(d.error || 'Save failed', 'error'); return; }
      toast('User saved', 'success');
      onSaved({ ...user, ...d });
    } catch { toast('Network error', 'error'); }
    finally { setSaving(false); }
  }

  async function handleDelete() {
    try {
      const res = await fetch(`/api/admin/org-users/${user.id}`, { method:'DELETE', headers: authH() });
      if (!res.ok) { const d = await res.json(); toast(d.error || 'Failed', 'error'); return; }
      toast(`${user.first_name} ${user.last_name} deactivated`, 'info');
      onDeleted(user.id);
    } catch { toast('Network error', 'error'); }
  }

  const overrideCount = Object.keys(user.custom_permissions || {}).length;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:400, display:'flex', justifyContent:'flex-end' }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width:'min(600px,100vw)', background:'#0a0a0a', borderLeft:'1px solid rgba(255,255,255,0.08)', overflowY:'auto', display:'flex', flexDirection:'column' }}>
        {/* Header */}
        <div style={{ padding:'16px 20px', borderBottom:'1px solid rgba(255,255,255,0.07)', display:'flex', alignItems:'center', gap:'14px', position:'sticky', top:0, background:'#0a0a0a', zIndex:1 }}>
          <div style={{ width:'46px', height:'46px', borderRadius:'50%', background:`linear-gradient(135deg,${ROLE_META[user.role]?.color ?? '#3b82f6'}40,${ROLE_META[user.role]?.color ?? '#3b82f6'}20)`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'17px', fontWeight:700, color:ROLE_META[user.role]?.color ?? '#3b82f6', flexShrink:0 }}>
            {user.first_name[0]}{user.last_name[0]}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:'15px', fontWeight:700, color:'white' }}>{user.first_name} {user.last_name}</div>
            <div style={{ fontSize:'12px', color:'#555555' }}>{user.email}</div>
          </div>
          <span style={{ padding:'4px 10px', borderRadius:'4px', fontSize:'11px', fontWeight:700, background:`${STATUS_COLOR[user.status] ?? '#555555'}18`, color:STATUS_COLOR[user.status] ?? '#555555' }}>{user.status}</span>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,0.06)', border:'none', borderRadius:'6px', color:'#555555', width:'28px', height:'28px', fontSize:'14px', cursor:'pointer', flexShrink:0 }}>✕</button>
        </div>

        <div style={{ padding:'20px', display:'flex', flexDirection:'column', gap:'20px' }}>
          {/* Info strip */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'10px' }}>
            {[
              { label:'Organisation', value: user.org_name || `Org #${user.organisation_id}`, locked:true },
              { label:'Member Since', value: user.created_at?.slice(0,10) || '—' },
              { label:'Last Login',   value: user.last_login || 'Never' },
            ].map(f => (
              <div key={f.label} style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'4px', padding:'12px' }}>
                <div style={{ fontSize:'10px', fontWeight:700, color:'#555555', textTransform:'uppercase', marginBottom:'4px' }}>{f.label}{f.locked && <span style={{ marginLeft:'4px', fontSize:'9px' }}>🔒</span>}</div>
                <div style={{ fontSize:'13px', fontWeight:600, color: f.locked ? '#555555' : '#e2e8f0' }}>{f.value}</div>
              </div>
            ))}
          </div>

          {/* Role selection */}
          <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'5px', padding:'16px' }}>
            <div style={{ fontSize:'13px', fontWeight:700, color:'white', marginBottom:'10px' }}>Base Role</div>
            <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', marginBottom:'8px' }}>
              {ROLE_ORDER.filter(r => isSuperAdmin || r !== 'admin').map(r => (
                <button key={r} onClick={() => setUser(u => ({...u, role:r}))}
                  style={{ padding:'6px 14px', borderRadius:'4px', border:`1px solid ${user.role===r?ROLE_META[r].color:'rgba(255,255,255,0.08)'}`, background:user.role===r?`${ROLE_META[r].color}18`:'transparent', color:user.role===r?ROLE_META[r].color:'#555555', fontSize:'12px', fontWeight:600, cursor:'pointer' }}>
                  {ROLE_META[r].label}
                </button>
              ))}
            </div>
            <div style={{ fontSize:'11px', color:'#555555' }}>
              {ROLE_META[user.role]?.label} grants {STD_ROLE_PERMS[user.role]?.length ?? 0} permissions by default.
            </div>
          </div>

          {/* Status */}
          <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'5px', padding:'16px' }}>
            <div style={{ fontSize:'13px', fontWeight:700, color:'white', marginBottom:'10px' }}>Account Status</div>
            <div style={{ display:'flex', gap:'8px' }}>
              {(['active','suspended'] as const).map(s => (
                <button key={s} onClick={() => setUser(u => ({...u, status:s}))}
                  style={{ padding:'7px 18px', borderRadius:'4px', border:`1px solid ${user.status===s ? STATUS_COLOR[s] : 'rgba(255,255,255,0.08)'}`, background:user.status===s?`${STATUS_COLOR[s]}18`:'transparent', color:user.status===s?STATUS_COLOR[s]:'#555555', fontSize:'12px', fontWeight:600, cursor:'pointer', textTransform:'capitalize' }}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Permission overrides */}
          <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'5px', padding:'16px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'10px', gap:'8px', flexWrap:'wrap' }}>
              <div>
                <div style={{ fontSize:'13px', fontWeight:700, color:'white', marginBottom:'3px' }}>
                  Permission Overrides
                  {overrideCount > 0 && <span style={{ marginLeft:'8px', padding:'2px 8px', borderRadius:'4px', fontSize:'10px', fontWeight:700, background:'rgba(59,130,246,0.15)', color:'#60a5fa' }}>{overrideCount} override{overrideCount !== 1 ? 's' : ''}</span>}
                </div>
                <div style={{ fontSize:'11px', color:'#555555' }}>Click a permission to cycle: inherit → grant → revoke</div>
              </div>
              <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
                {customRoles.length > 0 && (
                  <select onChange={e => { if (e.target.value) applyTemplate(e.target.value); e.target.value = ''; }}
                    style={{ padding:'5px 10px', background:'rgba(59,130,246,0.1)', border:'1px solid rgba(59,130,246,0.3)', borderRadius:'7px', color:'#60a5fa', fontSize:'11px', cursor:'pointer' }}>
                    <option value="">Apply template…</option>
                    {customRoles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                )}
                {overrideCount > 0 && (
                  <button onClick={resetOverrides} style={{ padding:'5px 12px', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.25)', borderRadius:'7px', color:'#f87171', fontSize:'11px', fontWeight:600, cursor:'pointer' }}>
                    Clear All
                  </button>
                )}
              </div>
            </div>
            <UserPermMatrix baseRole={user.role} overrides={user.custom_permissions || {}} onChange={cyclePermOverride} />
          </div>

          {/* Actions */}
          <div style={{ display:'flex', gap:'8px', alignItems:'center', flexWrap:'wrap' }}>
            <button onClick={handleSave} disabled={saving}
              style={{ padding:'10px 26px', background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', border:'none', borderRadius:'4px', color:'white', fontSize:'13px', fontWeight:700, cursor:'pointer', opacity:saving?0.6:1 }}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
            {!confirmDelete ? (
              <button onClick={() => setConfirmDelete(true)} style={{ padding:'10px 18px', background:'rgba(239,68,68,0.07)', border:'1px solid rgba(239,68,68,0.25)', borderRadius:'4px', color:'#f87171', fontSize:'12px', fontWeight:600, cursor:'pointer' }}>
                Deactivate User
              </button>
            ) : (
              <div style={{ display:'flex', gap:'6px', alignItems:'center' }}>
                <span style={{ fontSize:'12px', color:'#f87171' }}>Confirm deactivate?</span>
                <button onClick={handleDelete} style={{ padding:'7px 14px', background:'rgba(239,68,68,0.15)', border:'1px solid rgba(239,68,68,0.4)', borderRadius:'7px', color:'#f87171', fontSize:'12px', fontWeight:700, cursor:'pointer' }}>Yes</button>
                <button onClick={() => setConfirmDelete(false)} style={{ padding:'7px 12px', background:'transparent', border:'none', color:'#555555', fontSize:'12px', cursor:'pointer' }}>Cancel</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Role Templates Tab ───────────────────────────────────────────────────────

interface RoleTemplatesTabProps {
  orgId: number;
  roles: CustomRole[];
  onChange: (roles: CustomRole[]) => void;
}

function RoleTemplatesTab({ orgId, roles, onChange }: RoleTemplatesTabProps) {
  const { toast } = useToast();
  const [editing, setEditing] = useState<CustomRole | null>(null);   // null = closed, new Role = creating, existing = editing
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('#3b82f6');
  const [editGrants, setEditGrants] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  function openNew() {
    setEditing({ id:'', name:'', color:'#3b82f6', permissions:[] });
    setEditName(''); setEditColor('#3b82f6'); setEditGrants(new Set());
  }

  function openEdit(r: CustomRole) {
    setEditing(r);
    setEditName(r.name); setEditColor(r.color); setEditGrants(new Set(r.permissions));
  }

  function togglePerm(id: string) {
    setEditGrants(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  function applyBaseRole(role: StdRole) {
    setEditGrants(new Set(STD_ROLE_PERMS[role]));
  }

  async function handleSave() {
    if (!editName.trim()) { toast('Name required', 'error'); return; }
    setSaving(true);
    try {
      const isNew = !editing?.id;
      const url = isNew ? `/api/admin/orgs/${orgId}/custom-roles` : `/api/admin/orgs/${orgId}/custom-roles/${editing!.id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT', headers: authH(),
        body: JSON.stringify({ name: editName.trim(), color: editColor, permissions: Array.from(editGrants) }),
      });
      const d = await res.json();
      if (!res.ok) { toast(d.error || 'Save failed', 'error'); return; }
      if (isNew) {
        onChange([...roles, d]);
      } else {
        onChange(roles.map(r => r.id === d.id ? d : r));
      }
      toast(`Role "${d.name}" saved`, 'success');
      setEditing(null);
    } catch { toast('Network error', 'error'); }
    finally { setSaving(false); }
  }

  async function handleDelete(r: CustomRole) {
    try {
      const res = await fetch(`/api/admin/orgs/${orgId}/custom-roles/${r.id}`, { method:'DELETE', headers: authH() });
      if (!res.ok) { const d = await res.json(); toast(d.error || 'Delete failed', 'error'); return; }
      onChange(roles.filter(x => x.id !== r.id));
      toast(`"${r.name}" deleted`, 'info');
    } catch { toast('Network error', 'error'); }
  }

  if (editing !== null) {
    return (
      <div style={{ padding:'20px' }}>
        <button onClick={() => setEditing(null)} style={{ padding:'6px 14px', background:'rgba(255,255,255,0.05)', border:'none', borderRadius:'4px', color:'#a0a0a0', fontSize:'12px', cursor:'pointer', marginBottom:'16px' }}>← Back to Templates</button>
        <div style={{ maxWidth:'760px' }}>
          <div style={{ fontSize:'15px', fontWeight:700, color:'white', marginBottom:'16px' }}>{editing.id ? `Edit: ${editing.name}` : 'New Role Template'}</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:'12px', marginBottom:'16px', alignItems:'flex-end' }}>
            <div>
              <label style={{ fontSize:'12px', fontWeight:600, color:'#a0a0a0', display:'block', marginBottom:'5px' }}>Template Name</label>
              <input value={editName} onChange={e => setEditName(e.target.value)} placeholder="e.g. Night Shift Operator"
                style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'4px', padding:'9px 12px', color:'#e5e5e5', fontSize:'13px', outline:'none', boxSizing:'border-box' as const }} />
            </div>
            <div>
              <label style={{ fontSize:'12px', fontWeight:600, color:'#a0a0a0', display:'block', marginBottom:'5px' }}>Colour</label>
              <div style={{ display:'flex', gap:'5px', flexWrap:'wrap' }}>
                {ROLE_COLORS.map(c => (
                  <button key={c} onClick={() => setEditColor(c)}
                    style={{ width:'22px', height:'22px', borderRadius:'50%', background:c, border:`2px solid ${editColor===c?'white':'transparent'}`, cursor:'pointer', outline:'none' }} />
                ))}
              </div>
            </div>
          </div>
          <div style={{ marginBottom:'12px' }}>
            <div style={{ fontSize:'12px', fontWeight:600, color:'#a0a0a0', marginBottom:'8px' }}>Quick-fill from standard role</div>
            <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
              {ROLE_ORDER.map(r => (
                <button key={r} onClick={() => applyBaseRole(r)}
                  style={{ padding:'5px 12px', borderRadius:'7px', border:`1px solid ${ROLE_META[r].color}50`, background:`${ROLE_META[r].color}10`, color:ROLE_META[r].color, fontSize:'11px', fontWeight:600, cursor:'pointer' }}>
                  {ROLE_META[r].label} ({STD_ROLE_PERMS[r].length})
                </button>
              ))}
              <button onClick={() => setEditGrants(new Set())}
                style={{ padding:'5px 12px', borderRadius:'7px', border:'1px solid rgba(255,255,255,0.1)', background:'transparent', color:'#555555', fontSize:'11px', fontWeight:600, cursor:'pointer' }}>
                Clear All
              </button>
            </div>
          </div>
          <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'5px', padding:'16px', marginBottom:'16px' }}>
            <div style={{ fontSize:'13px', fontWeight:700, color:'white', marginBottom:'12px' }}>
              Permissions
              <span style={{ marginLeft:'8px', fontSize:'11px', fontWeight:400, color:'#555555' }}>{editGrants.size} of {ALL_PERMISSIONS.length} selected</span>
            </div>
            <PermMatrix grants={editGrants} onChange={togglePerm} />
          </div>
          <div style={{ display:'flex', gap:'8px' }}>
            <button onClick={handleSave} disabled={saving}
              style={{ padding:'10px 26px', background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', border:'none', borderRadius:'4px', color:'white', fontSize:'13px', fontWeight:700, cursor:'pointer', opacity:saving?0.6:1 }}>
              {saving ? 'Saving…' : 'Save Template'}
            </button>
            <button onClick={() => setEditing(null)} style={{ padding:'10px 18px', background:'#111111', border:'1px solid #1a1a1a', borderRadius:'4px', color:'#555555', fontSize:'13px', cursor:'pointer' }}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding:'20px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'20px', flexWrap:'wrap', gap:'12px' }}>
        <div>
          <div style={{ fontSize:'14px', fontWeight:700, color:'white', marginBottom:'3px' }}>Custom Role Templates</div>
          <div style={{ fontSize:'12px', color:'#555555' }}>Define named permission presets. Apply them when creating users, or as a base for individual overrides.</div>
        </div>
        <button onClick={openNew} style={{ padding:'8px 18px', background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', border:'none', borderRadius:'4px', color:'white', fontSize:'12px', fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>+ New Template</button>
      </div>

      {roles.length === 0 ? (
        <div style={{ padding:'40px', textAlign:'center', color:'#555555' }}>
          <div style={{ fontSize:'32px', marginBottom:'12px' }}>🔐</div>
          <div style={{ fontSize:'14px', fontWeight:600, color:'#555555', marginBottom:'6px' }}>No custom templates yet</div>
          <div style={{ fontSize:'12px', color:'#555555', marginBottom:'16px' }}>Create a template to define a specific permission set — e.g. "Night Shift Operator", "CCTV Viewer Only".</div>
          <button onClick={openNew} style={{ padding:'9px 22px', background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', border:'none', borderRadius:'4px', color:'white', fontSize:'13px', fontWeight:700, cursor:'pointer' }}>Create First Template</button>
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:'12px' }}>
          {roles.map(r => (
            <div key={r.id} style={{ background:'#0d0d0d', border:`1px solid ${r.color}25`, borderRadius:'5px', padding:'16px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'10px' }}>
                <div>
                  <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'3px' }}>
                    <span style={{ width:'10px', height:'10px', borderRadius:'50%', background:r.color, display:'inline-block', flexShrink:0 }} />
                    <span style={{ fontSize:'13px', fontWeight:700, color:'white' }}>{r.name}</span>
                  </div>
                  <div style={{ fontSize:'11px', color:'#555555' }}>{r.permissions.length} permission{r.permissions.length !== 1 ? 's' : ''} · Created by {r.created_by ?? '—'}</div>
                </div>
                <div style={{ display:'flex', gap:'5px' }}>
                  <button onClick={() => openEdit(r)} style={{ padding:'4px 10px', background:'rgba(59,130,246,0.1)', border:'1px solid rgba(59,130,246,0.3)', borderRadius:'6px', color:'#3b82f6', fontSize:'11px', fontWeight:600, cursor:'pointer' }}>Edit</button>
                  <button onClick={() => handleDelete(r)} style={{ padding:'4px 10px', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.25)', borderRadius:'6px', color:'#f87171', fontSize:'11px', fontWeight:600, cursor:'pointer' }}>Delete</button>
                </div>
              </div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:'4px' }}>
                {r.permissions.slice(0, 8).map(pid => {
                  const p = ALL_PERMISSIONS.find(x => x.id === pid);
                  return p ? (
                    <span key={pid} style={{ padding:'2px 7px', background:`${r.color}12`, border:`1px solid ${r.color}30`, borderRadius:'5px', fontSize:'10px', color:r.color }}>{p.label}</span>
                  ) : null;
                })}
                {r.permissions.length > 8 && <span style={{ fontSize:'10px', color:'#555555', padding:'2px 6px' }}>+{r.permissions.length - 8} more</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Standard Roles Reference Tab ─────────────────────────────────────────────

function StandardRolesTab() {
  return (
    <div style={{ padding:'20px' }}>
      <div style={{ fontSize:'14px', fontWeight:700, color:'white', marginBottom:'4px' }}>Standard Role Reference</div>
      <div style={{ fontSize:'12px', color:'#555555', marginBottom:'20px' }}>These are the built-in roles. Use Role Templates to create custom permission presets on top of these.</div>
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px', minWidth:'640px' }}>
          <thead>
            <tr>
              <th style={{ padding:'10px 14px', textAlign:'left', fontSize:'10px', fontWeight:700, color:'#444444', textTransform:'uppercase', letterSpacing:'0.5px', minWidth:'200px', position:'sticky', left:0, background:'#0a0a0a', zIndex:5 }}>Permission</th>
              {ROLE_ORDER.map(r => (
                <th key={r} style={{ padding:'10px 14px', textAlign:'center', minWidth:'110px' }}>
                  <span style={{ padding:'3px 10px', borderRadius:'4px', fontSize:'10px', fontWeight:700, background:`${ROLE_META[r].color}18`, color:ROLE_META[r].color }}>{ROLE_META[r].label}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {CATEGORIES.map(cat => (
              <React.Fragment key={cat}>
                <tr>
                  <td colSpan={ROLE_ORDER.length + 1} style={{ padding:'8px 14px 4px', fontSize:'10px', fontWeight:700, color:'#444444', letterSpacing:'1px', textTransform:'uppercase', background:'#0d0d0d', borderTop:'1px solid rgba(255,255,255,0.06)' }}>{cat}</td>
                </tr>
                {ALL_PERMISSIONS.filter(p => p.category === cat).map(perm => (
                  <tr key={perm.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.03)' }}>
                    <td style={{ padding:'8px 14px', position:'sticky', left:0, background:'#0a0a0a', zIndex:4 }}>
                      <div style={{ fontSize:'12px', color:'#e5e5e5', fontWeight:500 }}>{perm.label}</div>
                      <div style={{ fontSize:'10px', color:'#555555' }}>{perm.description}</div>
                    </td>
                    {ROLE_ORDER.map(r => {
                      const has = (STD_ROLE_PERMS[r] || []).includes(perm.id);
                      return (
                        <td key={r} style={{ textAlign:'center', padding:'8px' }}>
                          <div style={{ width:'20px', height:'20px', borderRadius:'5px', margin:'0 auto', background: has ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.03)', border:`1px solid ${has?'rgba(34,197,94,0.3)':'rgba(255,255,255,0.07)'}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'11px', color:'#22c55e' }}>
                            {has ? '✓' : ''}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function UserManagement() {
  const { user: me } = useAuth();
  const { toast } = useToast();
  const isSuperAdmin = !!(me as any)?.is_super_admin;
  const isOrgAdmin   = (me as any)?.role === 'admin';
  const canManage    = isSuperAdmin || isOrgAdmin;
  const myOrgId      = (me as any)?.organisation_id as number | null;

  const [users, setUsers]           = useState<OrgUser[]>([]);
  const [loading, setLoading]       = useState(true);
  const [customRoles, setCustomRoles] = useState<CustomRole[]>([]);
  const [tab, setTab]               = useState<'users' | 'templates' | 'std-roles'>('users');
  const [search, setSearch]         = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAdd, setShowAdd]       = useState(false);
  const [drawer, setDrawer]         = useState<OrgUser | null>(null);

  // Determine which org we're managing (super admin can switch)
  // Super admins default to "all orgs" view — their own org_id is irrelevant here
  const [viewOrgId, setViewOrgId] = useState<number | null>(isSuperAdmin ? null : myOrgId);
  const [orgs, setOrgs]           = useState<{id:number;name:string}[]>([]);

  useEffect(() => {
    if (!canManage) return;
    // Load users
    const url = isSuperAdmin && viewOrgId ? `/api/admin/org-users?org_id=${viewOrgId}` : '/api/admin/org-users';
    fetch(url, { headers: authH() })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.users) setUsers(d.users); })
      .catch(() => {})
      .finally(() => setLoading(false));
    // Load custom roles
    const orgId = viewOrgId ?? myOrgId;
    if (orgId) {
      fetch(`/api/admin/orgs/${orgId}/custom-roles`, { headers: authH() })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d?.roles) setCustomRoles(d.roles); })
        .catch(() => {});
    }
  }, [viewOrgId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isSuperAdmin) return;
    fetch('/api/admin/orgs', { headers: authH() })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.orgs) setOrgs(d.orgs); })
      .catch(() => {});
  }, [isSuperAdmin]);

  const filtered = users.filter(u => {
    if (roleFilter   !== 'all' && u.role   !== roleFilter)   return false;
    if (statusFilter !== 'all' && u.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (`${u.first_name} ${u.last_name} ${u.email}`).toLowerCase().includes(q);
    }
    return true;
  });

  function handleUserAdded(u: OrgUser) { setUsers(p => [u, ...p]); }
  function handleUserSaved(u: OrgUser) { setUsers(p => p.map(x => x.id === u.id ? u : x)); setDrawer(u); }
  function handleUserDeleted(id: number) { setUsers(p => p.filter(x => x.id !== id)); setDrawer(null); }

  const tabStyle = (t: string): React.CSSProperties => ({
    padding:'7px 18px', border:'none', background:'none', cursor:'pointer', fontSize:'13px', fontWeight:600,
    color: tab===t ? '#e2e8f0' : '#555555',
    borderBottom: tab===t ? '2px solid #3b82f6' : '2px solid transparent',
    transition:'all 0.15s',
  });

  return (
    <Layout title="User Management" subtitle="Manage users, roles, and granular permissions for your organisation">
      {showAdd && (
        <AddUserModal
          isSuperAdmin={isSuperAdmin}
          callerOrgId={viewOrgId ?? myOrgId}
          customRoles={customRoles}
          onAdd={handleUserAdded}
          onClose={() => setShowAdd(false)}
        />
      )}
      {drawer && (
        <UserDrawer
          user={drawer}
          customRoles={customRoles}
          isSuperAdmin={isSuperAdmin}
          isOrgAdmin={isOrgAdmin}
          onClose={() => setDrawer(null)}
          onSaved={handleUserSaved}
          onDeleted={handleUserDeleted}
        />
      )}

      {/* Super admin org picker */}
      {isSuperAdmin && orgs.length > 0 && (
        <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 16px', marginBottom:'16px', background:'rgba(168,85,247,0.06)', border:'1px solid rgba(168,85,247,0.2)', borderRadius:'4px', fontSize:'12px', flexWrap:'wrap' }}>
          <span style={{ color:'#a855f7', fontWeight:700 }}>★ Super Admin</span>
          <span style={{ color:'#555555' }}>Viewing org:</span>
          <select value={viewOrgId ?? ''} onChange={e => { setLoading(true); setUsers([]); setViewOrgId(Number(e.target.value) || null); }}
            style={{ padding:'5px 10px', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(168,85,247,0.3)', borderRadius:'7px', color:'#e5e5e5', fontSize:'12px', cursor:'pointer' }}>
            <option value="">— All orgs —</option>
            {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
      )}

      {/* Summary tiles */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'12px', marginBottom:'20px' }}>
        {[
          { label:'Total Users',   value: users.length,                                  color:'#e5e5e5', icon:'👥' },
          { label:'Active',        value: users.filter(u=>u.status==='active').length,    color:'#22c55e', icon:'✅' },
          { label:'MFA Enabled',   value: users.filter(u=>u.mfa_enabled).length,          color:'#3b82f6', icon:'🔐' },
          { label:'Role Templates', value: customRoles.length,                            color:'#a855f7', icon:'🎭' },
        ].map(s => (
          <div key={s.label} style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'5px', padding:'16px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'8px' }}>
              <span style={{ fontSize:'11px', color:'#555555' }}>{s.label}</span>
              <span style={{ fontSize:'18px' }}>{s.icon}</span>
            </div>
            <div style={{ fontSize:'26px', fontWeight:700, color:s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Main card */}
      <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'5px', overflow:'hidden' }}>
        {/* Tab bar */}
        <div style={{ padding:'0 20px', borderBottom:'1px solid #1a1a1a', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'8px' }}>
          <div style={{ display:'flex' }}>
            <button style={tabStyle('users')}     onClick={() => setTab('users')}>👥 Users</button>
            <button style={tabStyle('templates')} onClick={() => setTab('templates')}>🎭 Role Templates</button>
            <button style={tabStyle('std-roles')} onClick={() => setTab('std-roles')}>📋 Standard Roles</button>
          </div>
          {tab === 'users' && canManage && (
            <button onClick={() => setShowAdd(true)} style={{ padding:'6px 16px', background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', border:'none', borderRadius:'4px', color:'white', fontSize:'12px', fontWeight:700, cursor:'pointer' }}>
              + Add User
            </button>
          )}
        </div>

        {/* ── Users tab ── */}
        {tab === 'users' && (
          <div>
            <div style={{ padding:'12px 20px', borderBottom:'1px solid rgba(255,255,255,0.04)', display:'flex', gap:'8px', flexWrap:'wrap', alignItems:'center' }}>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or email…"
                style={{ flex:1, minWidth:'160px', padding:'7px 12px', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'4px', color:'#e5e5e5', fontSize:'12px', outline:'none' }} />
              <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
                style={{ padding:'7px 12px', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'4px', color:'#e5e5e5', fontSize:'12px', cursor:'pointer' }}>
                <option value="all">All Roles</option>
                {ROLE_ORDER.map(r => <option key={r} value={r}>{ROLE_META[r].label}</option>)}
              </select>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                style={{ padding:'7px 12px', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'4px', color:'#e5e5e5', fontSize:'12px', cursor:'pointer' }}>
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
              <button onClick={() => exportCSV(`users-${csvDateStamp()}`,
                ['ID','Name','Email','Role','Status','MFA','Last Login','Created'],
                filtered.map(u=>[u.id,`${u.first_name} ${u.last_name}`,u.email,u.role,u.status,u.mfa_enabled?'Yes':'No',u.last_login,u.created_at?.slice(0,10)||''])
              )} style={{ padding:'7px 14px', background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.25)', borderRadius:'4px', color:'#22c55e', fontSize:'12px', fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
                ↓ Export
              </button>
            </div>

            {loading ? (
              Array.from({length:5}).map((_,i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:'16px', padding:'11px 20px', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                  <div style={{ width:'36px', height:'36px', borderRadius:'50%', background:'rgba(255,255,255,0.05)', flexShrink:0 }} />
                  <div style={{ flex:1 }}>
                    <div style={{ height:'12px', width:'140px', background:'rgba(255,255,255,0.05)', borderRadius:'4px', marginBottom:'5px' }} />
                    <div style={{ height:'10px', width:'180px', background:'#111111', borderRadius:'4px' }} />
                  </div>
                </div>
              ))
            ) : filtered.length === 0 ? (
              <div style={{ padding:'40px', textAlign:'center', color:'#555555' }}>
                <div style={{ fontSize:'28px', marginBottom:'10px' }}>👤</div>
                <div style={{ fontSize:'14px', color:'#555555' }}>{users.length === 0 ? 'No users found. Add the first user to get started.' : 'No users match your filters.'}</div>
              </div>
            ) : (
              <>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom:'1px solid #1a1a1a' }}>
                      {['User','Role','Status','MFA','Last Login','Overrides',''].map(h => (
                        <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:'10px', fontWeight:700, color:'#555555', textTransform:'uppercase', letterSpacing:'0.06em', whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(u => {
                      const roleMeta = ROLE_META[u.role] ?? { label: u.role, color: '#555555' };
                      const overrideCount = Object.keys(u.custom_permissions || {}).length;
                      return (
                        <tr key={u.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)', cursor:'pointer' }}
                          onMouseEnter={e => (e.currentTarget.style.background='rgba(255,255,255,0.02)')}
                          onMouseLeave={e => (e.currentTarget.style.background='transparent')}
                          onClick={() => setDrawer(u)}>
                          <td style={{ padding:'11px 16px' }}>
                            <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                              <div style={{ width:'34px', height:'34px', borderRadius:'50%', background:`linear-gradient(135deg,${roleMeta.color}40,${roleMeta.color}20)`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'12px', fontWeight:700, color:roleMeta.color, flexShrink:0 }}>
                                {u.first_name[0]}{u.last_name[0]}
                              </div>
                              <div>
                                <div style={{ fontSize:'13px', fontWeight:600, color:'#e5e5e5' }}>{u.first_name} {u.last_name}</div>
                                <div style={{ fontSize:'11px', color:'#555555' }}>{u.email}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{ padding:'11px 16px' }}>
                            <span style={{ padding:'3px 10px', borderRadius:'4px', fontSize:'10px', fontWeight:700, background:`${roleMeta.color}18`, color:roleMeta.color }}>{roleMeta.label}</span>
                          </td>
                          <td style={{ padding:'11px 16px' }}>
                            <span style={{ padding:'3px 10px', borderRadius:'4px', fontSize:'10px', fontWeight:700, background:`${STATUS_COLOR[u.status]??'#555555'}18`, color:STATUS_COLOR[u.status]??'#555555' }}>{u.status}</span>
                          </td>
                          <td style={{ padding:'11px 16px', textAlign:'center', fontSize:'14px' }}>{u.mfa_enabled ? '🔐' : <span style={{ color:'#444444', fontSize:'12px' }}>—</span>}</td>
                          <td style={{ padding:'11px 16px', fontSize:'11px', color:'#555555', whiteSpace:'nowrap' }}>{u.last_login}</td>
                          <td style={{ padding:'11px 16px' }}>
                            {overrideCount > 0
                              ? <span style={{ padding:'2px 8px', borderRadius:'4px', fontSize:'10px', fontWeight:700, background:'rgba(59,130,246,0.12)', color:'#60a5fa' }}>{overrideCount} override{overrideCount!==1?'s':''}</span>
                              : <span style={{ fontSize:'11px', color:'#444444' }}>Role default</span>}
                          </td>
                          <td style={{ padding:'11px 16px' }}>
                            <button onClick={e => { e.stopPropagation(); setDrawer(u); }} style={{ padding:'4px 12px', background:'rgba(59,130,246,0.1)', border:'1px solid rgba(59,130,246,0.3)', borderRadius:'6px', color:'#3b82f6', fontSize:'11px', fontWeight:600, cursor:'pointer' }}>
                              Manage →
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div style={{ padding:'8px 20px', borderTop:'1px solid rgba(255,255,255,0.04)', fontSize:'10px', color:'#444444' }}>
                  Showing {filtered.length} of {users.length} users
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Role Templates tab ── */}
        {tab === 'templates' && (
          <RoleTemplatesTab
            orgId={(viewOrgId ?? myOrgId) as number}
            roles={customRoles}
            onChange={setCustomRoles}
          />
        )}

        {/* ── Standard roles reference ── */}
        {tab === 'std-roles' && <StandardRolesTab />}
      </div>
    </Layout>
  );
}
